import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    PanelFrame,
    StatusBadge,
    DataTable,
    formatNumber,
    formatChipText,
    formatRelativeTime,
    INNER_RADIUS,
    accentColorFor,
} from './SharedFrames';
import { useViduraDecisions } from '../hooks/useViduraDecisions';

const decisionTone = (decision) => {
    const value = (decision || '').toString().toUpperCase();
    if (value === 'DEPLOY') return 'success';
    if (value === 'MISSED') return 'warning';
    return 'default'; // SKIP and everything else
};

const formatGatePair = (value, threshold, digits = 2) => {
    const left = value == null ? '—' : formatNumber(value, digits);
    const right = threshold == null ? '—' : formatNumber(threshold, digits);
    return `${left} / ${right}`;
};

// Hero gate gauge: green deploy-zone bar when ivRank > ivThreshold, neutral
// otherwise. The lockbox's alpha IS the gate, so this is the entire thesis.
const GateGauge = ({ ivRank, ivThreshold }) => {
    const rank = Number(ivRank);
    const threshold = Number(ivThreshold);
    const hasRank = ivRank != null && !Number.isNaN(rank);
    const hasThreshold = ivThreshold != null && !Number.isNaN(threshold);
    const inDeployZone = hasRank && hasThreshold && rank > threshold;
    // IV-rank is a 0..1 fraction; clamp for the bar fill.
    const fillPct = hasRank ? Math.min(100, Math.max(0, rank * 100)) : 0;
    const thresholdPct = hasThreshold ? Math.min(100, Math.max(0, threshold * 100)) : null;

    return (
        <Box>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
                    {`IV-rank ${formatGatePair(ivRank, ivThreshold)}`}
                </Typography>
                <StatusBadge
                    label={inDeployZone ? 'Deploy zone' : 'Skip zone'}
                    tone={inDeployZone ? 'success' : 'default'}
                />
            </Stack>
            <Box
                sx={(theme) => ({
                    position: 'relative',
                    height: 12,
                    borderRadius: 999,
                    overflow: 'hidden',
                    backgroundColor: alpha(theme.palette.divider, 0.5),
                })}
            >
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        inset: 0,
                        width: `${fillPct}%`,
                        borderRadius: 999,
                        backgroundColor: inDeployZone
                            ? accentColorFor(theme, 'success')
                            : alpha(accentColorFor(theme, 'neutral'), 0.55),
                        transition: 'width 0.3s ease',
                    })}
                />
                {thresholdPct != null ? (
                    <Box
                        sx={(theme) => ({
                            position: 'absolute',
                            top: -2,
                            bottom: -2,
                            left: `${thresholdPct}%`,
                            width: 2,
                            backgroundColor: theme.palette.text.primary,
                            opacity: 0.7,
                        })}
                    />
                ) : null}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block', lineHeight: 1.45 }}>
                Clean order-book-mid IV-rank must clear the threshold to deploy. The gate is the entire thesis —
                Vidura is a frozen paper lockbox, zero conviction, paper-only.
            </Typography>
        </Box>
    );
};

const ViduraGatePanel = ({ gate, enabled = true }) => {
    const { decisions, loading, error } = useViduraDecisions(enabled, 50);

    if (!enabled) {
        return null;
    }

    const g = gate || {};
    const recent = (decisions || []).slice(0, 20);

    const decisionRows = recent.map((d, index) => ({
        ...d,
        _key: `${d.tradingDay || ''}-${d.expiry || ''}-${index}`,
    }));

    return (
        <PanelFrame
            accent="strategy"
            eyebrow="Lockbox Gate"
            title="Vidura Gate"
            description="Frozen paper lockbox — the clean order-book-mid IV-rank gate is the entire thesis. Zero conviction, paper-only."
            action={
                <StatusBadge
                    label={g.lastDecision ? formatChipText(g.lastDecision) : 'No decision'}
                    tone={decisionTone(g.lastDecision)}
                />
            }
        >
            <Stack spacing={2}>
                <Box
                    sx={(theme) => ({
                        borderRadius: INNER_RADIUS,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.82),
                        backgroundColor: theme.palette.mode === 'dark'
                            ? alpha('#0f172a', 0.58)
                            : alpha('#ffffff', 0.88),
                        px: 1.4,
                        py: 1.25,
                    })}
                >
                    <GateGauge ivRank={g.ivRank} ivThreshold={g.ivThreshold} />
                </Box>

                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                    <StatusBadge
                        label={`Regime ${formatChipText(g.regime || '—')}`}
                        tone={g.regime === 'clean' ? 'success' : g.regime === 'trans' ? 'warning' : 'default'}
                    />
                    <StatusBadge
                        label={`Last ${formatChipText(g.lastDecision || '—')}${g.lastDecisionAt ? ` · ${formatRelativeTime(g.lastDecisionAt)}` : ''}`}
                        tone={decisionTone(g.lastDecision)}
                    />
                    <StatusBadge
                        label={`ATM IV ${g.atmIvMid != null ? formatNumber(g.atmIvMid, 2) : '—'}`}
                        tone="default"
                    />
                    <StatusBadge
                        label={`Next expiry ${g.nextExpiry || '—'}`}
                        tone="default"
                    />
                </Stack>

                {g.lastReason ? (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {`Last reason: ${g.lastReason}`}
                    </Typography>
                ) : null}

                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                        Recent decisions
                    </Typography>
                    {error ? (
                        <Typography variant="body2" color="error.main">
                            {`Could not load decisions — ${error}`}
                        </Typography>
                    ) : (
                        <DataTable
                            title="vidura-decisions"
                            framed={false}
                            columns={[
                                { key: 'tradingDay', label: 'Trading Day' },
                                { key: 'expiry', label: 'Expiry' },
                                {
                                    key: 'ivRank',
                                    label: 'IV-rank',
                                    align: 'right',
                                    render: (value) => (value == null ? '—' : formatNumber(value, 2)),
                                },
                                {
                                    key: 'decision',
                                    label: 'Decision',
                                    render: (value) => (
                                        <StatusBadge
                                            label={formatChipText(value || '—')}
                                            tone={decisionTone(value)}
                                        />
                                    ),
                                },
                                { key: 'reason', label: 'Reason' },
                                {
                                    key: 'execCredit',
                                    label: 'Exec Credit',
                                    align: 'right',
                                    render: (value) => (value == null ? '—' : formatNumber(value, 2)),
                                },
                            ]}
                            rows={decisionRows}
                            emptyMessage={loading ? 'Loading decisions…' : 'No gate decisions recorded yet.'}
                        />
                    )}
                </Box>
            </Stack>
        </PanelFrame>
    );
};

export default ViduraGatePanel;
